import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";
import type { ThreeWayBasis } from "../../../../components/PortfolioPerformance/basis-toggle";

export interface PdPoint {
  month: string; // "12mo", "24mo", ...
  // One pd{year} key per cohort the chart returns for the chosen toggle position -- not a
  // fixed set, so this isn't typed as individual optional fields.
  [key: `pd${number}`]: number | string;
}

export interface ExpectedLossSummary {
  expectedLossDollars: string;
  // Real latest file date, e.g. "Mar'26" -- replaces the old hardcoded
  // "2025" in this tile's own label.
  asOf: string;
  // null -- the real LGD table is still WIP. Render an empty state for just
  // this tile, not a fabricated %.
  /** Probability of Default in %: the Net Loss Rate at the latest file date (Loss & Recovery's chart); null when loss_recovery is not usable */
  probabilityOfDefault: number | null;
  lgdBackbone: number | null;
  exposureAtDefault: string;
  riskAppetitePct: number;
}

export interface PdCurveSummary {
  pdCurve: PdPoint[];
  // One entry per cohort in pdCurve -- "actual" once any age has realized,
  // "projected" otherwise.
  cohortKind: Record<string, "actual" | "projected">;
  // cohort year -> the month labels ("12mo", ...) that are realized readings; every other
  // month on that cohort's curve is projected. Drives the solid/dotted split.
  realized: Record<string, string[]>;
}

export function useExpectedLoss() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["expected-loss", ticket ?? "all"],
    queryFn: () => apiGet<ExpectedLossSummary>("/expected-loss", { ticket }),
  });
}

/** The PD chart. The toggle picks the rows that are realized, projected, or of active cohorts. */
export function usePdCurve(basis: ThreeWayBasis) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["expected-loss", "pd-curve", basis, ticket ?? "all"],
    queryFn: () => apiGet<PdCurveSummary>("/expected-loss/pd-curve", { ticket, basis }),
  });
}
